/* eslint-disable */

/**
 * Traps focus to an element/element with focusable elements
 * @param {Node | Node[]} $elements A single node/array of nodes whose child elements/self will be focused on
 *
 * Usage Example:
 * let modal = document.querySelector('.js-modal-with-focus-elements');
 * let modalToggle = document.querySelector('.js-modal-toggle'); //optional
 * let focustrapInstance = new FocusTrap([modalToggle, modal]);
 *
 * focustrapInstance.set(); //Tabbing focus is now trapped to elements within modal and toggle
 * focustrapInstance.unset(); //Tabbing focus is removed and entire DOM can be traversed using tab
* */

import { FocusEligibilityFinder } from 'shared/assets/javascript/accessibility/focusEligibilityFinder';

class FocusTrap {
  constructor($elements) {
    this.dom = {
      $elements: [].concat($elements || []),
      $focusableContent: null,
      $firstFocusableElement: null,
      $lastFocusableElement: null,
    };
    this.focusEligibilityFinder = new FocusEligibilityFinder({ requireTabEligible: true });
    this.keydownEvent = this.keydownEvent.bind(this);
    this.refreshAccessibilityEvent = this.refreshAccessibilityEvent.bind(this);
  }

  set() {
    this.setFocusableContent();
    this.focusOnFirstElement();
    this.setEventListener();
  }

  unset() {
    this.removeEventListener();
  }

  setFocusableContent() {
    const { focusEligibilityFinder } = this;
    const { $elements } = this.dom;
    let $focusableContent = [];

    $elements.forEach(($element) => {
      const $focusableElements = focusEligibilityFinder.findFrom($element);

      $focusableContent = [
        ...$focusableContent,
        ...$focusableElements,
      ];
    });

    const $firstFocusableElement = $focusableContent[0]; // get first element to be focused inside modal
    const $lastFocusableElement = $focusableContent[$focusableContent.length - 1]; // get last element to be focused inside modal

    this.dom.$focusableContent = $focusableContent;
    this.dom.$firstFocusableElement = $firstFocusableElement;
    this.dom.$lastFocusableElement = $lastFocusableElement;
  }

  setEventListener() {
    const { keydownEvent, refreshAccessibilityEvent } = this;
    document.addEventListener('keydown', keydownEvent);
    document.addEventListener('refreshAccessibility', refreshAccessibilityEvent);
  }

  removeEventListener() {
    const { keydownEvent, refreshAccessibilityEvent } = this;
    document.removeEventListener('keydown', keydownEvent);
    document.removeEventListener('refreshAccessibility', refreshAccessibilityEvent);
  }

  refreshAccessibilityEvent(event) {
    setTimeout(() => {
      this.setFocusableContent();
    }, 25);
  }

  keydownEvent(event) {
    const { $firstFocusableElement, $lastFocusableElement } = this.dom;
    const isTabPressed = event.key === 'Tab';

    if (!isTabPressed) {
      return;
    }

    if (event.shiftKey) { // if shift key pressed for shift + tab combination
      if (document.activeElement === $firstFocusableElement) {
        this.focusOnLastElement();
        event.preventDefault();
      }
    } else { // if tab key is pressed
      if (document.activeElement === $lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
        this.focusOnFirstElement();
        event.preventDefault();
      }
    }
  }

  focusOnFirstElement() {
    const { $firstFocusableElement } = this.dom;
    $firstFocusableElement.focus();
  }

  focusOnLastElement() {
    const { $lastFocusableElement } = this.dom;
    $lastFocusableElement.focus();
  }
}

export { FocusTrap };